import type { RequestFn } from '../http';

/**
 * Kundenberichte (Monat/Quartal/Jahr): erzeugen, kuratieren, freigeben.
 * Techniker-Seite — die Kundensicht liefert `getReportCustomerView`.
 */

export type ReportPeriodType = 'MONTH' | 'QUARTER' | 'YEAR';
export type ReportStatus = 'DRAFT' | 'IN_REVIEW' | 'RELEASED';
export type ReportItemKind = 'CRITICAL' | 'POSITIVE' | 'IMPROVEMENT' | 'UPSELL';
export type ReportGoalOwner = 'FIXBYTE' | 'CUSTOMER' | 'JOINT';
export type ReportGoalStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'DROPPED';

export interface ReportListEntry {
  id: string;
  title: string;
  periodType: ReportPeriodType;
  periodStart: string;
  periodEnd: string;
  status: ReportStatus;
  visibleInPortal: boolean;
  generatedAt: string;
  generatedByName: string | null;
  releasedAt: string | null;
  releasedByName: string | null;
  _count: { items: number; goals: number; tickets: number };
}

export interface ReportItem {
  id: string;
  kind: ReportItemKind;
  title: string;
  body: string;
  include: boolean;
  sensitivity: 'NEUTRAL' | 'SENSITIVE';
  rationale: string | null;
  evidence: { ticketNumbers?: string[] } | null;
  source: 'AI' | 'MANUAL';
  sortOrder: number;
  offeringKey: string | null;
  effortHint: string | null;
  valueHint: string | null;
}

export interface ReportGoal {
  id: string;
  title: string;
  detail: string | null;
  owner: ReportGoalOwner;
  status: ReportGoalStatus;
  dueDate: string | null;
  include: boolean;
  sortOrder: number;
  carriedFromGoalId: string | null;
}

export interface ReportTicket {
  id: string;
  weclappTicketId: string | null;
  ticketNumber: string | null;
  subject: string;
  status: string | null;
  priority: string | null;
  openedAt: string | null;
  closedAt: string | null;
  firstResponseMinutes: number | null;
  /** Erstkontakt: Kommentar ODER Telefonat (eingehend angenommen / ausgehender Versuch) */
  firstContactMinutes: number | null;
  firstContactSource: 'COMMENT' | 'CALL' | null;
  callCount: number;
  callMinutes: number;
  resolutionHours: number | null;
  slaResponseMet: boolean | null;
  slaResolutionMet: boolean | null;
  hoursBooked: number;
  preview: string | null;
  diagnosis: string | null;
  include: boolean;
  sortOrder: number;
}

export interface ChannelMetricsView {
  calls: {
    total: number;
    inbound: number;
    outbound: number;
    inboundMissed: number;
    talkMinutes: number;
  };
  chats: {
    conversations: number;
    messagesIn: number;
    messagesOut: number;
    avgFirstReplyMinutes: number | null;
    channels: string[];
  };
}

export interface ReportMetrics {
  periodLabel: string;
  totals: {
    ticketsOpened: number;
    ticketsClosed: number;
    hoursBooked: number;
    avgFirstResponseHours: number | null;
    avgFirstContactHours: number | null;
    avgResolutionHours: number | null;
    calls: number;
    chats: number;
  };
  previous:
    | {
        periodLabel: string;
        ticketsOpened: number;
        ticketsClosed: number;
        hoursBooked: number;
        avgFirstResponseHours: number | null;
        avgResolutionHours: number | null;
        calls: number;
        chats: number;
      }
    | null;
  openAtPeriodEnd: number;
  sla: {
    responseHours: number;
    resolutionHours: number;
    responseEvaluated: number;
    resolutionEvaluated: number;
    responseMetPercent: number | null;
    resolutionMetPercent: number | null;
    agreementNumber: string | null;
  } | null;
  byPriority: { name: string; count: number }[];
  /** Telefonate und Chats des Zeitraums */
  channels: ChannelMetricsView & { calls: { callbackTasks: number; callbackTasksOpen: number; matchedNumbers: number } };
  byTechnician: { name: string; hours: number }[];
  /**
   * Eingeplantes Kontingent gegen tatsächlichen Verbrauch — intern. Ältere Berichte
   * kennen den Block noch nicht, und ohne hinterlegtes Kontingent bleibt er leer.
   */
  contingent?: ReportContingent | null;
  /** Verträge & Lizenzen — eingefrorener Stand; fehlt bei älteren Berichten */
  deadlines?: ReportDeadlinesView | null;
  mirrorSyncedAt: string | null;
  notes: string[];
}

export type ContingentVerdict =
  | 'UNDERUSED'
  | 'OK'
  | 'WATCH'
  | 'ADJUST_INTERNALLY'
  | 'RENEGOTIATE'
  | 'ADJUST_CONTRACT';

export interface ReportContingent {
  includedHoursPerMonth: number;
  includedHours: number;
  /** Aus dem Vorzeitraum mitgenommen; 0, wenn der Rest laut Vereinbarung verfällt. */
  carriedOverHours: number;
  /** Kontingent + Übertrag — daran misst sich die Auslastung. */
  availableHours: number;
  /** Fair-Use-Grenze: verfügbare Stunden × Toleranz. */
  ceilingHours: number;
  /** Die für diesen Zeitraum geltende Sprosse der Toleranzleiter. */
  tolerancePercent: number;
  toleranceLevel: 'SINGLE' | 'CONSECUTIVE';
  /** Ab wie viel Prozent des Kontingents die Marge aufgebraucht ist; null ohne Kalkulation. */
  marginGonePercent: number | null;
  usedHours: number;
  /** positiv = Rest, negativ = überzogen */
  balanceHours: number;
  utilizationPercent: number;
  overrun: boolean;
  /** Zeitpunkt im Zeitraum, an dem der Fair-Use-Rahmen aufgebraucht war. */
  exhaustedAt: string | null;
  ticketsAfterExhaustion: number;
  slaMissesAfterExhaustion: number;
  consecutiveOverruns: number;
  consecutiveUnderuse: number;
  history: {
    label: string;
    includedHours: number;
    usedHours: number;
    utilizationPercent: number;
    overrun: boolean;
  }[];
  /** Die letzten zwölf Monate am Stück, unabhängig vom Berichtszeitraum. */
  rollingYear: {
    from: string;
    until: string;
    includedHours: number;
    usedHours: number;
    utilizationPercent: number;
    over: boolean;
  } | null;
  /**
   * Was im Zeitraum neben der Pauschale lief — separat beauftragt (Angebot, Projekt)
   * und deshalb nicht gegen das Kontingent gezählt. Gehört trotzdem in die Ansicht:
   * Ein Monat mit einem großen Projekt sähe sonst aus wie ein ruhiger Monat.
   */
  separatelyBilled: {
    hours: number;
    quoteHours: number;
    projectHours: number;
    ticketCount: number;
  };
  /**
   * Auf Pauschal-Tickets gebucht, aber nicht gezählt: Fahrtzeit (wenn gesondert berechnet)
   * und Kulanz/nicht abrechenbar. Fehlt bei Berichten, die vor v1.378.1 berechnet wurden.
   */
  notCounted?: {
    travelHours: number;
    nonBillableHours: number;
    travelIncluded: boolean;
  };
  /** Was die vereinbarte Staffel daraus macht — null, solange sie nicht greift. */
  adjustment: {
    raisePercent: number;
    fromHoursPerMonth: number;
    toHoursPerMonth: number;
    effective: 'NEXT_YEAR' | 'NEXT_PERIOD';
  } | null;
  verdict: ContingentVerdict;
  /** Einzeiler der geltenden Vereinbarung. */
  fairUseSummary: string;
  basis: string;
}

export interface CustomerReportDetail {
  id: string;
  customerId: string;
  title: string;
  periodType: ReportPeriodType;
  periodStart: string;
  periodEnd: string;
  status: ReportStatus;
  summary: string | null;
  internalNote: string | null;
  metrics: ReportMetrics | Record<string, never>;
  visibleInPortal: boolean;
  /** Abschnitt „Verträge & Lizenzen“ im Kundenbericht zeigen */
  deadlinesInclude: boolean;
  generatedAt: string;
  generatedByName: string | null;
  releasedAt: string | null;
  releasedByName: string | null;
  llmModel: string | null;
  llmCostEur: string | number | null;
  items: ReportItem[];
  goals: ReportGoal[];
  tickets: ReportTicket[];
  customer: { id: string; name: string; weclappCustomerId: string | null; cwCompanyId: number | null };
}

/** Die Kundensicht — identisch mit dem, was Portal und PDF zeigen. */
export interface CustomerReportView {
  id: string;
  title: string;
  periodType: ReportPeriodType;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  releasedAt: string | null;
  customerName: string;
  summary: string | null;
  /** Verträge & Lizenzen — nur, wenn eingeschaltet und ein Stand vorliegt */
  deadlines: ReportDeadlinesView | null;
  metrics: {
    ticketsOpened: number;
    ticketsClosed: number;
    openAtPeriodEnd: number;
    hoursBooked: number;
    avgFirstResponseHours: number | null;
    avgFirstContactHours: number | null;
    avgResolutionHours: number | null;
    previous: {
      periodLabel: string;
      ticketsOpened: number;
      ticketsClosed: number;
      hoursBooked: number;
      calls: number;
      chats: number;
    } | null;
    sla: ReportMetrics['sla'];
    byPriority: { name: string; count: number }[];
    channels: ChannelMetricsView | null;
    notes: string[];
  } | null;
  sections: {
    kind: ReportItemKind;
    items: {
      id: string;
      title: string;
      body: string;
      ticketNumbers: string[];
      effortHint: string | null;
      valueHint: string | null;
    }[];
  }[];
  goals: {
    id: string;
    title: string;
    detail: string | null;
    owner: string;
    status: string;
    dueDate: string | null;
    carriedOver: boolean;
  }[];
  tickets: {
    id: string;
    ticketNumber: string | null;
    subject: string;
    status: string | null;
    priority: string | null;
    openedAt: string | null;
    closedAt: string | null;
    firstResponseMinutes: number | null;
    firstContactMinutes: number | null;
    firstContactSource: string | null;
    callCount: number;
    callMinutes: number;
    resolutionHours: number | null;
    slaResponseMet: boolean | null;
    slaResolutionMet: boolean | null;
    hoursBooked: number;
    preview: string | null;
    diagnosis: string | null;
  }[];
}

export interface ServiceOffering {
  key: string;
  name: string;
  /** Block im Portfolio; ältere Fassungen aus der Config können ihn noch nicht haben. */
  category?: string;
  pitch: string;
  trigger: string;
  effortHint: string;
  valueHint: string;
}

export interface GenerateReportInput {
  customerId: string;
  periodType: ReportPeriodType;
  /** YYYY-MM-DD; ohne Angabe der zuletzt abgeschlossene Zeitraum */
  periodStart?: string;
  withAi?: boolean;
}

/** URL des PDFs — cookie-authentifiziert, daher direkt im Browser aufrufbar. */
export function customerReportPdfUrl(reportId: string): string {
  return `/api/customer-reports/${encodeURIComponent(reportId)}/pdf`;
}

export function createCustomerReportsApi(request: RequestFn) {
  const base = '/customer-reports';
  const withId = (id: string) => `${base}/${encodeURIComponent(id)}`;

  return {
    async getCustomerReports(customerId: string) {
      return request<{ reports: ReportListEntry[] }>(
        `${base}?customerId=${encodeURIComponent(customerId)}`
      );
    },

    async getServicePortfolio() {
      return request<{ portfolio: ServiceOffering[] }>(`${base}/portfolio`);
    },

    async generateCustomerReport(input: GenerateReportInput) {
      return request<{ report: CustomerReportDetail; aiError: string | null }>(`${base}/generate`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    async getCustomerReport(id: string) {
      return request<{ report: CustomerReportDetail }>(withId(id));
    },

    async getCustomerReportView(id: string) {
      return request<{ view: CustomerReportView }>(`${withId(id)}/customer-view`);
    },

    async regenerateCustomerReport(id: string, withAi = true) {
      return request<{ report: CustomerReportDetail; aiError: string | null }>(`${withId(id)}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ withAi }),
      });
    },

    async updateCustomerReport(
      id: string,
      data: {
        title?: string;
        summary?: string;
        internalNote?: string;
        status?: 'DRAFT' | 'IN_REVIEW';
        deadlinesInclude?: boolean;
      }
    ) {
      return request<{ report: CustomerReportDetail }>(withId(id), {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async addCustomerReportItem(
      id: string,
      data: {
        kind: ReportItemKind;
        title: string;
        body: string;
        include?: boolean;
        offeringKey?: string;
        ticketNumbers?: string[];
      }
    ) {
      return request<{ report: CustomerReportDetail }>(`${withId(id)}/items`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateCustomerReportItem(
      id: string,
      itemId: string,
      data: { title?: string; body?: string; include?: boolean; sortOrder?: number }
    ) {
      return request<{ report: CustomerReportDetail }>(
        `${withId(id)}/items/${encodeURIComponent(itemId)}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    async deleteCustomerReportItem(id: string, itemId: string) {
      return request<{ report: CustomerReportDetail }>(
        `${withId(id)}/items/${encodeURIComponent(itemId)}`,
        { method: 'DELETE' }
      );
    },

    async addCustomerReportGoal(
      id: string,
      data: {
        title: string;
        detail?: string;
        owner?: ReportGoalOwner;
        dueDate?: string | null;
        include?: boolean;
      }
    ) {
      return request<{ report: CustomerReportDetail }>(`${withId(id)}/goals`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateCustomerReportGoal(
      id: string,
      goalId: string,
      data: {
        title?: string;
        detail?: string;
        owner?: ReportGoalOwner;
        status?: ReportGoalStatus;
        dueDate?: string | null;
        include?: boolean;
        sortOrder?: number;
      }
    ) {
      return request<{ report: CustomerReportDetail }>(
        `${withId(id)}/goals/${encodeURIComponent(goalId)}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    async deleteCustomerReportGoal(id: string, goalId: string) {
      return request<{ report: CustomerReportDetail }>(
        `${withId(id)}/goals/${encodeURIComponent(goalId)}`,
        { method: 'DELETE' }
      );
    },

    async updateCustomerReportTicket(
      id: string,
      ticketId: string,
      data: { preview?: string; diagnosis?: string; include?: boolean; sortOrder?: number }
    ) {
      return request<{ report: CustomerReportDetail }>(
        `${withId(id)}/tickets/${encodeURIComponent(ticketId)}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    async releaseCustomerReport(id: string, notifyInbox = true) {
      return request<{ report: CustomerReportDetail }>(`${withId(id)}/release`, {
        method: 'POST',
        body: JSON.stringify({ notifyInbox }),
      });
    },

    async revokeCustomerReport(id: string) {
      return request<{ report: CustomerReportDetail }>(`${withId(id)}/revoke`, { method: 'POST' });
    },
  };
}


// ─── Verträge & Lizenzen im Bericht ───────────────────────────────────────────

export type ReportDeadlineOutcome = 'RENEWS' | 'ENDS' | 'ACTION';

export interface ReportDeadlineRowView {
  name: string;
  category: string;
  provider: string | null;
  ownership: string;
  endDate: string;
  actionDate: string;
  actionIsCancellation: boolean;
  status: 'EXPIRED' | 'CRITICAL' | 'WARNING' | 'RENEWING' | 'ENDING' | 'OK';
  outcome: ReportDeadlineOutcome;
}

export interface ReportDeadlinesView {
  monitored: number;
  viaFixbyte: number;
  customerManaged: number;
  selfEntries: number;
  remindersSent: number;
  dueInPeriod: ReportDeadlineRowView[];
  dueInPeriodTotal: number;
  upcoming: ReportDeadlineRowView[];
  upcomingTotal: number;
  outlookDays: number;
}
